import styled from "@emotion/styled";
import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Settings } from "@livechat/design-system-icons";
import {
  DesignToken,
  Icon,
  SpacingToken,
  Switch,
  Text,
  Tooltip,
  UpdateBadge,
} from "@livechat/design-system-react-components";

import { Divider } from "components/divider/Divider";
import { newFeaturesCampaigns } from "constants/campaigns";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { AgentFormSectionId } from "routes/agents/agent-form/constants";
import { AgentsRoutes } from "routes/agents/constants";
import { AgentActions, selectLoggedInAgent } from "store/entities/agents";
import { selectCanChangeAutoassignment } from "store/entities/license";
import { withVar } from "themes/helpers";

import { NavigationOption } from "./styles";

interface IProps {
  hideProfileMenu: VoidFunction;
}

export const NavigationAutoassignmentSwitcher: FC<IProps> = ({ hideProfileMenu }) => {
  const dispatch = useDispatch();

  const {
    isNewForAgent: isAgentCreatedBeforeAgentAutoAssignmentManagementRelease,
    featureFlagValue: hasSeenAutoAssignmentSwitcherInProfileMenu,
  } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenAutoAssignmentSwitcherInProfileMenu.flag,
    newFeaturesCampaigns.hasSeenAutoAssignmentSwitcherInProfileMenu.campaignStartDate
  );
  const shouldDisplayAssignmentSwitcherNewBadge =
    isAgentCreatedBeforeAgentAutoAssignmentManagementRelease && !hasSeenAutoAssignmentSwitcherInProfileMenu;

  const canChangeAutoassignment = useSelector(selectCanChangeAutoassignment);
  const loggedInAgent = useSelector(selectLoggedInAgent);

  const shouldDisplayMenuItem = canChangeAutoassignment;

  const handleSettingsClick = (): void => {
    trackEngagement("Profile menu: Auto-assignment settings clicked", EventPlace.ProfileMenu);
    hideProfileMenu();
  };

  const handleAutoassignmentToggle = (_e: React.FormEvent, value: boolean): void => {
    dispatch(AgentActions.changeAutoAssignment({ autoassignment: value }));
    trackEngagement("Agents: Agent updated", EventPlace.ProfileMenu, {
      changedFields: ["autoassignment"],
      location: "Profile Popover",
      autoAssignmentState: value ? "0n" : "Off",
    });
    trackEngagement("Profile menu: Auto-assignment toggle clicked", EventPlace.ProfileMenu, {
      state: value ? "0n" : "Off",
    });
  };

  if (!shouldDisplayMenuItem) return null;

  return (
    <>
      <StyledNavigationOption>
        <Title>
          <Text size="sm" customColor={withVar(DesignToken.ContentBasicPrimary)}>
            Auto-assignment
          </Text>
          {shouldDisplayAssignmentSwitcherNewBadge && <UpdateBadge />}
        </Title>
        <ActionWrapper>
          <Tooltip
            kind="invert"
            placement="top"
            triggerRenderer={() => (
              <StyledLink
                to={`${AgentsRoutes.AgentsMeEdit}#${AgentFormSectionId.AutoAssignment}`}
                onClick={handleSettingsClick}
                data-testid="autoassignment-settings"
              >
                <Icon source={Settings} kind="subtle" />
              </StyledLink>
            )}
          >
            Go to auto-assignment settings
          </Tooltip>
          <Switch
            on={loggedInAgent.autoassignment}
            onChange={handleAutoassignmentToggle}
            size="compact"
            data-testid="autoassignment-switch"
          />
        </ActionWrapper>
      </StyledNavigationOption>
      <Divider />
    </>
  );
};

const StyledNavigationOption = styled(NavigationOption)`
  justify-content: space-between;
  gap: ${withVar(SpacingToken.Spacing2)};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing1)};
`;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing2)};
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;
